import { Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthApi } from '@features/login/data-access/auth-api';
import { ServiceNotification } from '../../../shared/services/notification/service-notification';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject = new BehaviorSubject<boolean>(false);

	private readonly storageOrigin = AuthInterceptor.parseOrigin(
		environment.URL_STORAGE_S3
	);

	constructor(
		private authService: AuthApi,
		private serviceNotification: ServiceNotification
	) {}

	private static parseOrigin(url: string): string {
		try {
			return new URL(url).origin;
		} catch {
			return '';
		}
	}

	private isStorageRequest(url: string): boolean {
		if (!this.storageOrigin) return false;
		try {
			return (
				new URL(url, window.location.href).origin === this.storageOrigin
			);
		} catch {
			return false;
		}
	}

	intercept(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {

		if (this.isStorageRequest(req.url)) {
			return next.handle(req);
		}

		const clonedReq = req.clone({
			withCredentials: true,
		});

		return next.handle(clonedReq).pipe(
			catchError((error: HttpErrorResponse) => {
				if (
					error.status === 401 &&
					!req.url.includes('/auth/refresh-token') &&
					!req.url.includes('/auth/signin')
				) {
					return this.handle401Error(clonedReq, next);
				}
				return throwError(() => error);
			})
		);
	}

	private handle401Error(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(false);

			return this.authService.refreshToken().pipe(
				switchMap(() => {

					this.refreshTokenSubject.next(true);

					return next.handle(
						req.clone({
							withCredentials: true,
						})
					);
				}),
				catchError((error) => {
					this.authService.logout();
					return throwError(() => error);
				}),
				finalize(() => {
					this.isRefreshing = false;
				})
			);
		} else {

			return this.refreshTokenSubject.pipe(
				filter((refreshed) => refreshed === true),
				take(1),
				switchMap(() =>
					next.handle(
						req.clone({
							withCredentials: true,
						})
					)
				)
			);
		}
	}
}
